import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import QuestionCard from "@/components/site/QuestionCard";

const ACCENT = "#004D40"; // teal

const QUESTIONS = [
  {
    q: "The power goes off in one room but not the rest of the house. Does a child know to check the MCB — and which switch a tripped one looks like?",
    f: "Most don't know the box exists. The fix is often a single flipped switch and ten seconds of knowing where to look.",
  },
  {
    q: "Oil in a pan catches fire. What does a child reach for?",
    f: "Most reach for water — which turns a small flame into a fireball. The correct move, sliding a lid over the pan, is rarely taught and never forgotten once it is.",
  },
  {
    q: "There's a smell of gas in the kitchen. Does a child know the three things never to do?",
    f: "Never switch on a light, never use a phone near it, never re-enter before airing it out. A switch spark is enough to ignite a room.",
  },
  {
    q: "Someone gets a bad burn at home. What goes on it first?",
    f: "Most families reach for toothpaste, butter, or ice — all of which make it worse. Twenty minutes under cool running water is the actual first aid.",
  },
  {
    q: "A grandparent slips and falls. Does a child know whether to move them, and what to check first?",
    f: "The instinct is to lift them up immediately — which can worsen an injury. A simple check-first sequence prevents the most common harm.",
  },
  {
    q: "A younger sibling is choking and cannot speak. Does the older child know what to do in the next thirty seconds?",
    f: "Those thirty seconds decide the outcome. The back-blow and abdominal-thrust technique can be learned by any 11-year-old.",
  },
  {
    q: "The electricity bill suddenly triples. Can a child read the meter and the bill well enough to find out why?",
    f: "Most adults can't either. Reading units consumed turns a scary bill into a solvable question — usually a single appliance.",
  },
];

const AREAS = [
  { tag: "A", t: "Home Safety & Emergencies", d: "Electrical, water, gas, first aid, fire." },
  { tag: "B", t: "Home Operations", d: "Kitchen safety, appliances, cleaning, smart shopping, household money." },
  { tag: "C", t: "Health & Wellbeing", d: "Sleep, emotions, puberty, safe use of medicines, caring for elders." },
  { tag: "D", t: "Digital Safety", d: "Passwords, online fraud, using AI wisely, social media safety." },
  { tag: "E", t: "Responsibility", d: "Communication, handling conflict, understanding family finances, setting goals." },
];

export default function Level1() {
  useReveal();
  useEffect(() => { document.title = "Level 1 — Home · LifeIQ"; }, []);

  return (
    <main>
      {/* HERO */}
      <section
        className="bg-[#0D1B2A] text-white relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(13,27,42,0.95) 0%, rgba(13,27,42,0.97) 100%), url('https://images.unsplash.com/photo-1497486751825-1233686d5d80?auto=format&fit=crop&w=1920&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "center right",
        }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: ACCENT }} />
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-28">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#F57F17" }}>
              Level 1 · Home Intelligence · Age 10–12
            </div>
            <h1 className="mt-5 font-serif-d text-[42px] sm:text-6xl lg:text-[68px] leading-[1.04] tracking-tight">
              Can a child handle the home alone when something goes wrong?
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              Most homes assume an adult will always be there. They won't always be. Level 1 turns a 10 to 12-year-old into someone who can handle electrical, water, gas, fire, kitchen, and medical situations calmly and correctly — without panic, and without waiting for help that may be an hour away.
            </p>
            <p className="mt-6 font-serif-d italic text-[17px] sm:text-[19px] leading-[1.5] max-w-3xl" style={{ color: "#5BD0BD" }}>
              Home Intelligence — closing the gap between a child and their immediate environment.
            </p>
          </div>
        </div>
      </section>

      {/* DISCOVERY QUESTIONS */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Before we explain Level 1 — try these.</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Ordinary afternoons. Honest answers.
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] leading-[1.7]">
              These are ordinary situations any child can face on an ordinary afternoon. Notice how many have a clear, correct answer that is almost never taught.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {QUESTIONS.map((c, i) => <QuestionCard key={i} q={c.q} f={c.f} i={i} accent={ACCENT} />)}
          </div>

          <div className="mt-16 max-w-3xl reveal">
            <p className="font-serif-d text-[26px] sm:text-[32px] leading-[1.3]" style={{ color: ACCENT }}>
              Every one of these has a correct answer a child can learn — and practise until it becomes automatic. That is all of Level 1.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT LEVEL 1 COVERS */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>What Level 1 covers</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Five areas. One outcome: a child who can run and protect a home.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {AREAS.map((a, i) => (
              <div key={a.tag} data-testid={`area-${a.tag}`} className="reveal bg-white border border-[#E5E7EA] rounded-sm p-7 flex flex-col" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="h-1 w-10" style={{ backgroundColor: ACCENT }} />
                <div className="mt-5 text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Area {a.tag}</div>
                <h3 className="mt-2 font-serif-d text-[22px] text-[#0D1B2A] leading-tight">{a.t}</h3>
                <p className="mt-3 text-[15px] text-[#5C6470] leading-[1.65]">{a.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOME */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">By the end of Level 1</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08] text-white">
              A child who is an asset in a crisis.
            </h2>
            <p className="mt-7 text-[17px] sm:text-[19px] text-white/85 leading-[1.75]">
              A child can handle any issue that arises inside the home independently — without calling a parent in a panic, without a middleman, without freezing. They become an asset in a crisis instead of one more person to protect. That confidence quietly changes how they carry themselves everywhere else.
            </p>
            <Link to="/level-2" data-testid="level1-next" className="mt-10 inline-flex items-center gap-2 bg-[#F57F17] text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition rounded-sm">
              See Level 2 — Society <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
